import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Header,
  Content,
  Table,
  TableColumn,
  Link,
  HeaderLabel,
} from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { Chip, makeStyles } from '@material-ui/core';

interface TeamListItem {
  id: string;
  displayName: string;
  sourceFile: string;
  accessTypes: string[];
}

const ACCESS_TYPE_COLORS: Record<string, string> = {
  GitLab: '#e24329',
  AWS: '#ff9900',
  'Union.AI': '#6c47ff',
  Vault: '#000000',
  Linear: '#5e6ad2',
  Tailscale: '#222222',
  'Incident.io': '#f25533',
  Cloudsmith: '#2e86de',
  ArgoCD: '#ef7b4d',
  'GitLab.com': '#e24329',
};

const useStyles = makeStyles({
  chip: {
    margin: 2,
    color: '#fff',
    fontWeight: 500,
    fontSize: '0.75rem',
  },
});

export const AccountAccessListPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const baseUrl = await discoveryApi.getBaseUrl('account-access');
      const response = await fetchApi.fetch(`${baseUrl}/teams`);
      if (!response.ok)
        throw new Error(`Failed to fetch teams: ${response.statusText}`);
      setTeams(await response.json());
      setError(undefined);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: TableColumn<TeamListItem>[] = [
    {
      title: 'Team',
      field: 'displayName',
      highlight: true,
      render: row => (
        <Link to={`/account-access/${row.id}`}>{row.displayName}</Link>
      ),
    },
    {
      title: 'Source',
      field: 'sourceFile',
      render: row => (
        <code style={{ fontSize: '0.85em' }}>{row.sourceFile}</code>
      ),
    },
    {
      title: 'Access Types',
      field: 'accessTypes',
      sorting: false,
      render: row => (
        <>
          {row.accessTypes.map(type => (
            <Chip
              key={type}
              label={type}
              size="small"
              className={classes.chip}
              style={{
                backgroundColor: ACCESS_TYPE_COLORS[type] ?? '#607d8b',
              }}
            />
          ))}
        </>
      ),
    },
  ];

  if (error) {
    return (
      <Page themeId="tool">
        <Header title="Account Access" />
        <Content>
          <p>Error loading teams: {error}</p>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header title="Account Access" subtitle="Team access across services">
        <HeaderLabel label="Teams" value={String(teams.length)} />
      </Header>
      <Content>
        <Table
          title="All Teams"
          columns={columns}
          data={teams}
          isLoading={loading}
          options={{
            search: true,
            paging: true,
            pageSize: 20,
            pageSizeOptions: [20, 50, 100],
            padding: 'dense',
          }}
        />
      </Content>
    </Page>
  );
};
